// @ts-strict-ignore
import { antGrey, appHomeCardsWidgetTitleStyle, runwayColors } from '@/components/styles/globalStyles';
import { iconAndAltForIntegrationType } from '@/helpers/integrationHelpers';
import {
    AppStoreAvailableBuild,
    Integration,
    IntegrationInstallationId,
    IntegrationType,
    ReleaseStepType,
    RolloutMonitoring,
    SubscriptionFeatureType,
    DestinationPhasedRelease,
} from '@/types';
import { GroupedMonitoringStatistic } from '@/typescriptHelpers';
import { massageMonitoringData, monitoringStatisticsForStepInstallations } from '@/helpers';
import { MonitoringMetricCard, monitoringMetricCardsForStatistic } from '@/components/monitoring/MonitoringMetricCard';
import { useSelector } from 'react-redux';
import { selectMonitoringMetricsById, selectStepConfigsByType } from '@/selectors/selectors';
import { RootState } from '@/reducers/reducers';
import Space from 'antd/lib/space';
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import Progress from 'antd/lib/progress';
import Tooltip from 'antd/lib/tooltip';
import { getIntegrationTypeForAppStoreType } from '@/helpers';
import { displayTextForPausedOrAcceleratedPhasedReleaseGroup } from '@/helpers/displayHelpers';
import { BlockOutlined, ZoomInOutlined, ZoomOutOutlined } from '@ant-design/icons';
import { Destination } from '@/types/destinations';
import { PhasedReleaseChartBase, PhasedReleaseDaysSummary } from '@/components/appHome/widgets/LiveReleaseChartWidget';
import { DestinationCounter } from '@/components/appHome/DestinationCounter';
import { PlanRestrictedFeatureWrapper } from '@/components/planRestrictions/PlanRestrictedFeatureWrapper';
import Button from 'antd/lib/button';
import { flatMap } from 'lodash';

export interface WorkflowRunPhasedReleaseWidgetsContainerProps {
    appId: string;
    phasedGroups: DestinationPhasedRelease[][];
    appStoreBuildsByDestStepConfigId: Record<string, AppStoreAvailableBuild[]>;
    stabilityIntegrations: Integration[];
    rolloutMonitoringByDestStepConfigId?: Record<string, RolloutMonitoring>;
    allDestinations: Destination[];
    selectedDestinationStepConfigIds: string[];
    userSelectedMultipleAPKBuildId?: string;
}

export const WorkflowRunPhasedReleaseWidgetsContainer: React.FC<WorkflowRunPhasedReleaseWidgetsContainerProps> = ({
    appId,
    phasedGroups,
    appStoreBuildsByDestStepConfigId,
    stabilityIntegrations,
    rolloutMonitoringByDestStepConfigId,
    userSelectedMultipleAPKBuildId,
}) => {
    const [expandedModule, setExpandedModule] = React.useState<Record<number, boolean>>({});

    if (!phasedGroups || phasedGroups.length === 0) {
        return null;
    }

    React.useEffect(() => {
        setExpandedModule({});
    }, []);

    return (
        <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Rollouts}>
            {(isDisabled) => (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'stretch',
                        gap: 10,
                    }}
                >
                    {phasedGroups.slice(0, isDisabled ? 1 : undefined).map((groupedPhasedReleases, i) => {
                        return (
                            <PhasedReleaseGroupWidget
                                key={i}
                                appId={appId}
                                groupKey={i}
                                phasedGroupData={groupedPhasedReleases}
                                appStoreBuildsByDestStepConfigId={appStoreBuildsByDestStepConfigId}
                                rolloutMonitoringByDestStepConfigId={rolloutMonitoringByDestStepConfigId}
                                userSelectedMultipleAPKBuildId={userSelectedMultipleAPKBuildId ?? null}
                                stabilityIntegrations={stabilityIntegrations}
                                isExpandedModule={expandedModule[i] === true}
                                setExpandedModule={setExpandedModule}
                                onlyShowAdoptionsOnExpand={phasedGroups.length > 2}
                            />
                        );
                    })}
                </div>
            )}
        </PlanRestrictedFeatureWrapper>
    );
};

export interface PhasedReleaseProgressSectionProps {
    destinations: Destination[];
    percentage: number;
    dayNumber: number;
    daysToPercent: number[];
    isAutomatedPhasedRollout: boolean;
    isAccelerated: boolean;
    isRunwayAcceleratedRollout: boolean;
    isHalted: boolean;
    isRunwayHalted: boolean;
}

export const PhasedReleaseProgressSection: React.FC<PhasedReleaseProgressSectionProps> = ({
    destinations,
    percentage,
    dayNumber,
    daysToPercent,
    isAutomatedPhasedRollout,
    isAccelerated,
    isRunwayAcceleratedRollout,
    isHalted,
    isRunwayHalted,
}) => {
    const groupStoreIntegrationIds = [
        ...new Set(destinations.map((dest) => getIntegrationTypeForAppStoreType(dest.appStoreType))),
    ];
    const iconsAndAltForAllStoreIntegrationTypes: [string, string][] = groupStoreIntegrationIds.map((integrationId) => {
        return iconAndAltForIntegrationType(integrationId);
    });

    return (
        <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Rollouts}>
            {(isDisabled) => (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        gap: 8,
                        alignItems: 'stretch',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Space
                            split={
                                <div
                                    style={{
                                        width: 1,
                                        height: 10,
                                        backgroundColor: antGrey.grey5,
                                    }}
                                />
                            }
                            size={8}
                        >
                            <Space>
                                {iconsAndAltForAllStoreIntegrationTypes.length === 1 && (
                                    <img
                                        src={iconsAndAltForAllStoreIntegrationTypes[0][0]}
                                        alt={iconsAndAltForAllStoreIntegrationTypes[0][1]}
                                        width={16}
                                        height={16}
                                    />
                                )}
                                {iconsAndAltForAllStoreIntegrationTypes.length > 1 && (
                                    <Tooltip
                                        title={
                                            <div
                                                style={{
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    alignItems: 'center',
                                                }}
                                            >
                                                {iconsAndAltForAllStoreIntegrationTypes.map(([icon, alt], i) => {
                                                    return <img key={i} src={icon} alt={alt} width={16} height={16} />;
                                                })}
                                            </div>
                                        }
                                        color={runwayColors.white}
                                    >
                                        <BlockOutlined style={{ color: antGrey.grey6 }} size={12} />
                                    </Tooltip>
                                )}
                                <Text
                                    style={{
                                        ...appHomeCardsWidgetTitleStyle,
                                    }}
                                >
                                    Phased Release
                                </Text>
                                <PlanRestrictedFeatureWrapper
                                    feature={SubscriptionFeatureType.Rollouts}
                                    requiresTooltip
                                    mini
                                />
                            </Space>
                            {(isAccelerated || isHalted) && (
                                <Text
                                    style={{
                                        ...appHomeCardsWidgetTitleStyle,
                                        color: isAccelerated ? runwayColors.statusGreen : runwayColors.statusRed,
                                    }}
                                >
                                    {displayTextForPausedOrAcceleratedPhasedReleaseGroup(
                                        isHalted,
                                        isRunwayHalted,
                                        isAccelerated,
                                        isRunwayAcceleratedRollout,
                                    )}
                                </Text>
                            )}
                        </Space>
                        {!isDisabled && (
                            <PhasedReleaseDaysSummary
                                percentage={percentage}
                                dayNumber={dayNumber}
                                daysToPercent={daysToPercent}
                                isAutomatedPhasedRollout={isAutomatedPhasedRollout}
                                isRunwayAcceleratedRollout={isAccelerated && isRunwayAcceleratedRollout}
                                isRunwayRolloutPaused={isHalted && isRunwayHalted}
                            />
                        )}
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            flexDirection: 'row',
                            alignItems: 'center',
                            gap: 10,
                        }}
                    >
                        {!isDisabled ?
                            <>
                                <div
                                    style={{
                                        flexBasis: '18px',
                                        flexShrink: 0,
                                        flexGrow: 0,
                                    }}
                                >
                                    <DestinationCounter destinations={destinations} size={18} />
                                </div>
                                <PhasedReleaseChartBase
                                    percentage={percentage}
                                    dayNumber={dayNumber}
                                    daysToPercent={daysToPercent}
                                    isAutomatedPhasedRollout={isAutomatedPhasedRollout}
                                    wasHalted={isHalted}
                                    wasAccelerated={isAccelerated}
                                />
                            </>
                        :   <Progress
                                className="widget-progress-bar"
                                strokeLinecap="round"
                                showInfo={false}
                                strokeColor={runwayColors.primary}
                                percent={0}
                                trailColor={antGrey.grey4}
                                style={{
                                    flexGrow: 1,
                                    margin: 0,
                                }}
                                size={['100%', 8]}
                            />
                        }
                    </div>
                </div>
            )}
        </PlanRestrictedFeatureWrapper>
    );
};

export interface AdoptionRateSectionProps {
    destinationAdoptions: [Destination, number][];
    stabIntegrationId?: IntegrationType;
    showExpandButton?: boolean;
    isExpanded?: boolean;
    onExpandToggle?: () => void;
    showTitle?: boolean;
    maxVisibleAdoptions?: number;
}

export const AdoptionRateSection: React.FC<AdoptionRateSectionProps> = ({
    destinationAdoptions,
    stabIntegrationId,
    showExpandButton = false,
    isExpanded = false,
    onExpandToggle,
    showTitle = true,
    maxVisibleAdoptions,
}) => {
    const haveStabIntegrationId = !!stabIntegrationId;
    const [stabIcon, stabAlt] = iconAndAltForIntegrationType(stabIntegrationId);
    const haveGroupAdoptions = destinationAdoptions.length > 0;

    const displayedAdoptions =
        maxVisibleAdoptions ? destinationAdoptions.slice(0, maxVisibleAdoptions) : destinationAdoptions;

    return (
        <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Rollouts}>
            {(isDisabled) => (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        flex: 1,
                        gap: 8,
                        alignItems: 'stretch',
                    }}
                >
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                        }}
                    >
                        <Space>
                            {showTitle && (
                                <>
                                    {haveStabIntegrationId && stabIcon && (
                                        <img src={stabIcon} alt={stabAlt} width={16} height={16} />
                                    )}
                                    <Text
                                        style={{
                                            ...appHomeCardsWidgetTitleStyle,
                                        }}
                                    >
                                        Adoption rate (sessions)
                                    </Text>
                                    <PlanRestrictedFeatureWrapper
                                        feature={SubscriptionFeatureType.Rollouts}
                                        requiresTooltip
                                        mini
                                    />
                                </>
                            )}
                        </Space>
                        {showExpandButton && onExpandToggle && (
                            <Button
                                onClick={onExpandToggle}
                                style={{
                                    height: 16,
                                    width: 16,
                                    padding: 0,
                                    margin: 0,
                                    borderWidth: 0,
                                }}
                                size={'middle'}
                                icon={
                                    isExpanded ?
                                        <ZoomOutOutlined
                                            style={{
                                                color: antGrey.grey6,
                                                verticalAlign: 'baseline',
                                                margin: 0,
                                            }}
                                            size={16}
                                        />
                                    :   <ZoomInOutlined
                                            style={{
                                                color: antGrey.grey6,
                                                verticalAlign: 'baseline',
                                                margin: 0,
                                            }}
                                            size={16}
                                        />
                                }
                                type="link"
                            />
                        )}
                    </div>
                    {showTitle && (
                        <div
                            style={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'stretch',
                                gap: 6,
                            }}
                        >
                            {haveGroupAdoptions &&
                                displayedAdoptions.map(([destination, adoption], i) => {
                                    return (
                                        <DestinationAdoptionWidgetRow
                                            key={`i=${i}&percent=${adoption}`}
                                            destination={destination}
                                            adoptionPercentage={adoption}
                                            obfuscateRowData={isDisabled}
                                        />
                                    );
                                })}
                            {!haveGroupAdoptions && (
                                <Text
                                    style={{
                                        color: antGrey.grey6,
                                        fontWeight: 300,
                                        fontSize: 12,
                                    }}
                                >
                                    No adoption rates known.
                                </Text>
                            )}
                        </div>
                    )}
                </div>
            )}
        </PlanRestrictedFeatureWrapper>
    );
};

export interface RolloutMonitoringMetricsSectionProps {
    appId: string;
    rolloutMonitoring?: RolloutMonitoring;
    selectedBuildIdentifier?: string;
    stepInstallationIds?: IntegrationInstallationId[];
}

export const RolloutMonitoringMetricsSection: React.FC<RolloutMonitoringMetricsSectionProps> = ({
    appId,
    rolloutMonitoring,
    selectedBuildIdentifier,
    stepInstallationIds,
}) => {
    const metricsById = useSelector((state: RootState) => selectMonitoringMetricsById(state, appId));

    const groupedStats: GroupedMonitoringStatistic[] = React.useMemo(() => {
        if (!rolloutMonitoring) {
            return [];
        }
        const stats = massageMonitoringData({
            monitoringStatistics: rolloutMonitoring.monitoringStatistics ?? [],
            monitoringAutomations: rolloutMonitoring.monitoringAutomations ?? [],
            metricsById,
            stepInstallationIds,
        });
        if (selectedBuildIdentifier) {
            return stats.filter((stat) => !stat.buildIdentifier || stat.buildIdentifier === selectedBuildIdentifier);
        }
        return stats;
    }, [rolloutMonitoring, metricsById, selectedBuildIdentifier, stepInstallationIds]);

    if (groupedStats.length === 0) {
        return null;
    }

    return (
        <div
            style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(232px, 1fr))',
                gap: 12,
            }}
        >
            {flatMap(groupedStats, monitoringMetricCardsForStatistic).map((stat) => (
                <MonitoringMetricCard key={stat.key} {...stat} />
            ))}
        </div>
    );
};

export const computeDestinationAdoptions = (
    destinations: Destination[],
    appStoreBuildsByDestStepConfigId: Record<string, AppStoreAvailableBuild[]>,
    userSelectedMultipleAPKBuildId?: string,
): Record<IntegrationInstallationId, [Destination, number][]> => {
    const adoptionsByInstallationId: Record<IntegrationInstallationId, [Destination, number][]> = {};

    for (const destination of destinations) {
        const destStepConfigId = destination?.stepConfigId;
        const availableDestBuilds = appStoreBuildsByDestStepConfigId[destStepConfigId];
        if (!availableDestBuilds?.length) {
            continue;
        }

        const buildsToConsider =
            userSelectedMultipleAPKBuildId ?
                availableDestBuilds.filter((build) =>
                    Object.values(build.stabilityInfos ?? {}).some(
                        (info) => info?.buildIdentifier === userSelectedMultipleAPKBuildId,
                    ),
                )
            :   availableDestBuilds.slice(0, 1);

        for (const build of buildsToConsider) {
            for (const [installationId, info] of Object.entries(build.stabilityInfos ?? {})) {
                if (!info?.sessionAdoption) continue;
                if (
                    userSelectedMultipleAPKBuildId &&
                    info.buildIdentifier &&
                    info.buildIdentifier !== userSelectedMultipleAPKBuildId
                ) {
                    continue;
                }
                if (!adoptionsByInstallationId[installationId]) {
                    adoptionsByInstallationId[installationId] = [];
                }
                adoptionsByInstallationId[installationId].push([destination, info.sessionAdoption]);
            }
        }
    }

    for (const installationId of Object.keys(adoptionsByInstallationId)) {
        adoptionsByInstallationId[installationId].sort((a, b) => b[1] - a[1]);
    }

    return adoptionsByInstallationId;
};

export interface PhasedReleaseGroupWidgetProps {
    appId: string;
    groupKey: number;
    phasedGroupData: DestinationPhasedRelease[];
    appStoreBuildsByDestStepConfigId: Record<string, AppStoreAvailableBuild[]>;
    rolloutMonitoringByDestStepConfigId?: Record<string, RolloutMonitoring>;
    userSelectedMultipleAPKBuildId?: string;
    stabilityIntegrations: Integration[];
    isExpandedModule: boolean;
    setExpandedModule: React.Dispatch<React.SetStateAction<Record<number, boolean>>>;
    onlyShowAdoptionsOnExpand: boolean;
    shouldHideAdoptions?: boolean;
}

export const PhasedReleaseGroupWidget: React.FC<PhasedReleaseGroupWidgetProps> = ({
    appId,
    groupKey,
    phasedGroupData,
    appStoreBuildsByDestStepConfigId,
    rolloutMonitoringByDestStepConfigId,
    userSelectedMultipleAPKBuildId,
    stabilityIntegrations,
    isExpandedModule,
    setExpandedModule,
    onlyShowAdoptionsOnExpand,
    shouldHideAdoptions,
}) => {
    const groupDestinations = phasedGroupData.map((destinationPhasedRelease) => destinationPhasedRelease.destination);

    const adoptionsByInstallationId = React.useMemo(() => {
        return computeDestinationAdoptions(
            groupDestinations,
            appStoreBuildsByDestStepConfigId,
            userSelectedMultipleAPKBuildId,
        );
    }, [phasedGroupData, appStoreBuildsByDestStepConfigId, userSelectedMultipleAPKBuildId]);

    const adoptionSections = React.useMemo(() => {
        if (stabilityIntegrations.length === 0) {
            return [];
        }
        return stabilityIntegrations.map((integration) => {
            const destinationAdoptions = adoptionsByInstallationId[integration.installationId] ?? [];
            return {
                installationId: integration.installationId,
                integrationId: integration.id,
                destinationAdoptions,
            };
        });
    }, [stabilityIntegrations, adoptionsByInstallationId]);

    const maxAdoptionsLength = adoptionSections.reduce(
        (max, section) => Math.max(max, section.destinationAdoptions.length),
        0,
    );

    // Live step configs, not stepRun.stepConfigSnapshot: snapshots are stamped at run creation and
    // drift to installations that no longer exist, which silently drops every stat (RUN-18353).
    const rolloutStepConfigs = useSelector((state: RootState) =>
        selectStepConfigsByType(state, appId, ReleaseStepType.Rollout),
    );

    const [groupRolloutMonitoring, groupStepInstallationIds]: [
        RolloutMonitoring | undefined,
        IntegrationInstallationId[] | undefined,
    ] = React.useMemo(() => {
        if (!rolloutMonitoringByDestStepConfigId) {
            return [undefined, undefined];
        }
        for (const destination of groupDestinations) {
            const monitoring = rolloutMonitoringByDestStepConfigId[destination?.stepConfigId];
            if (!monitoring) {
                continue;
            }

            const rolloutStep = Object.values(rolloutStepConfigs).find((stepConfig) =>
                stepConfig.destinationStepConfigIds?.includes(destination.stepConfigId),
            );

            return [
                {
                    ...monitoring,
                    monitoringStatistics: monitoringStatisticsForStepInstallations(
                        monitoring.monitoringStatistics ?? [],
                        rolloutStep?.integrationInstallationIds,
                    ),
                },
                rolloutStep?.integrationInstallationIds,
            ];
        }
        return [undefined, undefined];
    }, [rolloutMonitoringByDestStepConfigId, phasedGroupData, rolloutStepConfigs]);

    const firstGroupPhasedDatum = phasedGroupData[0];

    const groupPercent = firstGroupPhasedDatum.phasedReleasePercent;
    const groupDay = firstGroupPhasedDatum.dayNumber;
    const groupPhasedConfig = firstGroupPhasedDatum.daysToPercentSchedule;
    const groupRolloutsAutomated = firstGroupPhasedDatum.isAutomatedPhasedRollout;
    const groupIsAccelerated = firstGroupPhasedDatum.isAccelerated;
    const groupWasRWYAccelerated = firstGroupPhasedDatum.isRunwayAcceleratedRollout;
    const groupIsHalted = firstGroupPhasedDatum.isPaused;
    const groupWasRWYHalted = firstGroupPhasedDatum.isRunwayPaused;

    const handleExpandToggle = () => {
        if (isExpandedModule) {
            setExpandedModule({});
        } else {
            setExpandedModule({ [groupKey]: true });
        }
    };

    const showExpandButton = maxAdoptionsLength > 5 || onlyShowAdoptionsOnExpand;

    return (
        <PlanRestrictedFeatureWrapper feature={SubscriptionFeatureType.Rollouts}>
            {() => (
                <div
                    style={{
                        position: 'relative',
                        width: '100%',
                        height: '100%',
                    }}
                >
                    {/* Expanded overlay */}
                    <div
                        style={{
                            position: 'absolute',
                            top: -1,
                            left: -1,
                            right: 1,
                            gap: 20,
                            width: 'calc(100% + 2px)',
                            zIndex: 1,
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            borderRadius: 4,
                            backgroundColor: runwayColors.white,
                            border: shouldHideAdoptions ? 'none' : `1px solid ${antGrey.grey5}`,
                            padding: '16px 20px',
                            boxSizing: 'border-box',
                            marginBottom: 8,
                            boxShadow: isExpandedModule && !shouldHideAdoptions ? 'var(--rw-shadow-md)' : null,
                            transition: 'all 0.2s ease-in-out',
                            ...(isExpandedModule ?
                                {
                                    opacity: 1,
                                }
                            :   {
                                    opacity: 0,
                                    pointerEvents: 'none',
                                }),
                        }}
                    >
                        <PhasedReleaseProgressSection
                            destinations={groupDestinations}
                            percentage={groupPercent}
                            dayNumber={groupDay}
                            daysToPercent={groupPhasedConfig}
                            isAutomatedPhasedRollout={groupRolloutsAutomated}
                            isAccelerated={groupIsAccelerated}
                            isRunwayAcceleratedRollout={groupWasRWYAccelerated}
                            isHalted={groupIsHalted}
                            isRunwayHalted={groupWasRWYHalted}
                        />
                        {!shouldHideAdoptions &&
                            adoptionSections.map((section) => (
                                <AdoptionRateSection
                                    key={`overlay-${section.installationId}`}
                                    destinationAdoptions={section.destinationAdoptions}
                                    stabIntegrationId={section.integrationId}
                                    showExpandButton={showExpandButton}
                                    isExpanded={isExpandedModule}
                                    onExpandToggle={handleExpandToggle}
                                    showTitle={true}
                                />
                            ))}
                        <RolloutMonitoringMetricsSection
                            appId={appId}
                            rolloutMonitoring={groupRolloutMonitoring}
                            selectedBuildIdentifier={userSelectedMultipleAPKBuildId}
                            stepInstallationIds={groupStepInstallationIds}
                        />
                    </div>
                    <div
                        style={{
                            display: 'flex',
                            gap: 20,
                            flexDirection: 'column',
                            alignItems: 'stretch',
                            borderRadius: 4,
                            backgroundColor: runwayColors.white,
                            border: `1px solid ${antGrey.grey5}`,
                            padding: '16px 20px',
                            boxSizing: 'border-box',
                        }}
                    >
                        <PhasedReleaseProgressSection
                            destinations={groupDestinations}
                            percentage={groupPercent}
                            dayNumber={groupDay}
                            daysToPercent={groupPhasedConfig}
                            isAutomatedPhasedRollout={groupRolloutsAutomated}
                            isAccelerated={groupIsAccelerated}
                            isRunwayAcceleratedRollout={groupWasRWYAccelerated}
                            isHalted={groupIsHalted}
                            isRunwayHalted={groupWasRWYHalted}
                        />
                        {!shouldHideAdoptions &&
                            adoptionSections.map((section) => (
                                <AdoptionRateSection
                                    key={`main-${section.installationId}`}
                                    destinationAdoptions={section.destinationAdoptions}
                                    stabIntegrationId={section.integrationId}
                                    showExpandButton={showExpandButton}
                                    isExpanded={isExpandedModule}
                                    onExpandToggle={handleExpandToggle}
                                    showTitle={!onlyShowAdoptionsOnExpand}
                                    maxVisibleAdoptions={onlyShowAdoptionsOnExpand ? undefined : 5}
                                />
                            ))}
                        {!onlyShowAdoptionsOnExpand && (
                            <RolloutMonitoringMetricsSection
                                appId={appId}
                                rolloutMonitoring={groupRolloutMonitoring}
                                selectedBuildIdentifier={userSelectedMultipleAPKBuildId}
                                stepInstallationIds={groupStepInstallationIds}
                            />
                        )}
                    </div>
                </div>
            )}
        </PlanRestrictedFeatureWrapper>
    );
};

interface DestinationAdoptionWidgetRowProps {
    destination: Destination;
    adoptionPercentage: number;
    obfuscateRowData: boolean;
}

export const DestinationAdoptionWidgetRow: React.FC<DestinationAdoptionWidgetRowProps> = ({
    destination,
    adoptionPercentage,
    obfuscateRowData,
}) => {
    return (
        <div
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'nowrap',
                fontSize: 10,
                height: 22,
                lineHeight: '22px',
                flexShrink: 0,
                flexGrow: 0,
            }}
        >
            <div
                style={{
                    flexShrink: 0,
                    flexGrow: 0,
                    flexBasis: '18px',
                }}
            >
                <DestinationCounter destinations={[destination]} size={18} />
            </div>
            <Progress
                className="widget-progress-bar"
                strokeLinecap="round"
                percent={obfuscateRowData ? 0 : adoptionPercentage}
                showInfo={false}
                strokeColor={runwayColors.primary}
                trailColor={antGrey.grey4}
                style={{
                    margin: 0,
                }}
                size={['100%', 8]}
            />
            {!obfuscateRowData && (
                <span
                    style={{
                        flexShrink: 0,
                        color: antGrey.grey7,
                        fontWeight: 300,
                        fontSize: 10,
                    }}
                >
                    {`${adoptionPercentage.toFixed(1)}%`}
                </span>
            )}
        </div>
    );
};
